import { z } from 'zod';

export const onboardingExperienceLevelSchema = z.enum([
  'student',
  'bootcamp',
  'self_taught',
  'junior',
]);

export type OnboardingExperienceLevel = z.infer<typeof onboardingExperienceLevelSchema>;

export const onboardingGoalSchema = z.enum([
  'first_job',
  'internship',
  'junior_interview',
  'current_job_growth',
]);

export type OnboardingGoal = z.infer<typeof onboardingGoalSchema>;

export const onboardingAiToolSchema = z.enum([
  'chatgpt',
  'copilot',
  'cursor',
  'claude',
  'gemini',
  'other',
]);

export type OnboardingAiTool = z.infer<typeof onboardingAiToolSchema>;

export const onboardingAiUsagePatternSchema = z.enum([
  'copy_answer',
  'ask_explanation',
  'debug_with_ai',
  'generate_tests',
]);

export type OnboardingAiUsagePattern = z.infer<typeof onboardingAiUsagePatternSchema>;

export const onboardingWeakAreaSchema = z.enum([
  'basics',
  'oop',
  'debugging',
  'data_structures',
  'web_backend',
  'interview_explanation',
]);

export type OnboardingWeakArea = z.infer<typeof onboardingWeakAreaSchema>;

/** Input collected during the onboarding flow (guest or authenticated). */
// BR-18: onboarding requires 1-6 AI tools, 1-4 usage patterns, and 1-3 weak
// areas, with constrained enums (4 experience, 4 goal, 6 tool, 4 usage, 6 weak
// area — see onboarding-options.ts). This schema is the four declaration
// answers; CV (PRE-105), skill review (PRE-111) and optional JD (PRE-112) sit
// in front. POST is gated by PRE-95 (cv + skills_review), not this payload.
export const onboardingInputSchema = z.object({
  experienceLevel: onboardingExperienceLevelSchema,
  goal: onboardingGoalSchema,
  aiTools: z.array(onboardingAiToolSchema).min(1).max(6),
  aiUsagePattern: z.array(onboardingAiUsagePatternSchema).min(1).max(4),
  weakAreas: z.array(onboardingWeakAreaSchema).min(1).max(3),
});

export type OnboardingInput = z.infer<typeof onboardingInputSchema>;

/** Profile slice returned by `GET /v1/me` (null when no user_profiles row). */
export const meProfileSchema = z.object({
  experienceLevel: onboardingExperienceLevelSchema,
  goal: onboardingGoalSchema,
  aiTools: z.array(onboardingAiToolSchema),
  aiUsagePattern: z.array(onboardingAiUsagePatternSchema),
  weakAreas: z.array(onboardingWeakAreaSchema),
  onboardingCompletedAt: z.string().nullable().optional(),
});

export type MeProfile = z.infer<typeof meProfileSchema>;

/** Full profile row returned by `PUT /v1/me/profile`. */
export const userProfileResponseSchema = meProfileSchema.extend({
  userId: z.string().min(1),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type UserProfileResponse = z.infer<typeof userProfileResponseSchema>;

export const onboardingStatusSchema = z.object({
  completed: z.boolean(),
  completedAt: z.string().datetime().nullish(),
});

export type OnboardingStatus = z.infer<typeof onboardingStatusSchema>;

/** Stored onboarding state associated with a user. */
export const onboardingStateSchema = onboardingInputSchema.extend({
  userId: z.string().min(1),
  completedAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type OnboardingState = z.infer<typeof onboardingStateSchema>;
